import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import ContributorList from '@/components/events/detail/tabs/contributors/contributors-list';
import { useMemo, useEffect } from 'react';
import { useEventStore } from '@/state/stores/use-event-store';
import { useGetEventContributorsQuery } from '@/state/queries/contributors/use-get-event-contributors';
import { useEventSessionsQuery } from '@/state/queries/sessions/use-event-sessions';
import { getAllFlowRelatedContributors } from '@/utils/contributor-extraction.utils';

export default function FlowContributorList({ flow }: { flow: FlowDto }) {
  const event = useEventStore((s) => s.event);
  const eventContributors = useEventStore((s) => s.contributors);
  const { setEventSessions, setEventSessionsPlan, setEventSessionsOrganize, setEventContributors } = useEventStore();
  // Use origin field for API call to match backend expectations
  // For event flows, use the event's origin ID, fallback to event ID
  const eventId = event?.origin || event?.id;

  const { data: allContributors, isLoading } = useGetEventContributorsQuery(eventId as string, { enabled: !!eventId });
  const { data: sessions, normalized: normalizedSessions } = useEventSessionsQuery({
    eventId: eventId as string,
    isInLibrary: false,
    loadAllModes: true,
  });

  // Populate eventSessions store with sessions for the current mode
  useEffect(() => {
    if (!sessions) return;
    const { planSessions, organizeSessions, preferredSessions } = normalizedSessions;
    if (planSessions.length > 0) {
      setEventSessionsPlan(planSessions);
    }
    setEventSessionsOrganize(organizeSessions);
    if (preferredSessions.length > 0) {
      setEventSessions(preferredSessions);
    }
  }, [sessions, normalizedSessions, setEventSessions, setEventSessionsPlan, setEventSessionsOrganize]);

  // Populate eventContributors store with contributors for the current mode
  useEffect(() => {
    if (allContributors) setEventContributors(allContributors);
  }, [allContributors, setEventContributors]);

  const flowContributors = useMemo(() => {
    // Use event store contributors if available, otherwise fall back to API data
    const contributorsToUse = eventContributors || allContributors;

    if (isLoading || !eventId) {
      // Fallback to deprecated contributors array if available
      return 'contributors' in flow && Array.isArray(flow.contributors)
        ? flow.contributors.map((fc) => fc.contributor)
        : [];
    }
    if (!contributorsToUse || !event) {
      // Fallback to deprecated contributors array if available
      return 'contributors' in flow && Array.isArray(flow.contributors)
        ? flow.contributors.map((fc) => fc.contributor)
        : [];
    }

    // Use the new utility function to get ALL flow-related contributors
    // This includes both direct flow contributors and session contributors in flow slots
    // For single-flow events (multiFlow=false), show ALL contributors
    const contributorsFromRoles = getAllFlowRelatedContributors(flow, contributorsToUse, event?.multiFlow);

    // If we have contributors from roles, use them
    if (contributorsFromRoles.length > 0) {
      return contributorsFromRoles;
    }

    // Fallback to deprecated contributors array if roles-based system returns empty
    return 'contributors' in flow && Array.isArray(flow.contributors)
      ? flow.contributors.map((fc) => fc.contributor)
      : [];
  }, [flow, eventContributors, allContributors, event, isLoading, eventId]);

  return <ContributorList contributors={flowContributors} isAddAllowed={!event?.multiFlow} flowContext={flow} />;
}
